import { useCallback, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { fetchMtrStructSubList } from '#/api/imsMgmt/settings/mtrStruct/sub/fetchList';
import { DEFAULT_LIST_PAGE_SIZE } from '#/utils/listUrl/listPaginationDefaults';
import { mergeFilterUpdate } from '#/modules/commonComponents/table/listFilterKeys';
import { mtrStructSubListQueryKey } from '#/utils/queryKeys';

const EMPTY_PAGE = {
  current_page: 1,
  page_size: DEFAULT_LIST_PAGE_SIZE,
  total_count: 0,
  total_page: 1,
};

const DEFAULT_LIST_QUERY = {
  page: 1,
  pageSize: DEFAULT_LIST_PAGE_SIZE,
  keyword: '',
  sort: [],
  filters: {},
};

function useMtrStructSubList(mtrStructMainId) {
  const [listQueryByMain, setListQueryByMain] = useState({
    mainId: null,
    query: DEFAULT_LIST_QUERY,
  });

  const listQuery = listQueryByMain.mainId === mtrStructMainId
    ? listQueryByMain.query
    : DEFAULT_LIST_QUERY;

  const updateListQuery = useCallback((updater) => {
    setListQueryByMain((prev) => {
      const current = prev.mainId === mtrStructMainId ? prev.query : DEFAULT_LIST_QUERY;
      const next = typeof updater === 'function' ? updater(current) : updater;
      return { mainId: mtrStructMainId, query: next };
    });
  }, [mtrStructMainId]);

  const apiListQuery = useMemo(() => ({
    ...listQuery,
    filters: {
      ...listQuery.filters,
      ...(mtrStructMainId ? { mtrStructMainId } : {}),
    },
  }), [listQuery, mtrStructMainId]);

  const query = useQuery({
    queryKey: mtrStructSubListQueryKey(apiListQuery),
    queryFn: () => fetchMtrStructSubList(apiListQuery),
    enabled: Boolean(mtrStructMainId),
    placeholderData: keepPreviousData,
  });

  const setPage = useCallback((page) => {
    updateListQuery((prev) => ({ ...prev, page }));
  }, [updateListQuery]);

  const setPageSize = useCallback((pageSize) => {
    updateListQuery((prev) => ({ ...prev, pageSize, page: 1 }));
  }, [updateListQuery]);

  const setKeyword = useCallback((keyword) => {
    updateListQuery((prev) => ({ ...prev, keyword, page: 1 }));
  }, [updateListQuery]);

  const setFilter = useCallback((key, value) => {
    updateListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: mergeFilterUpdate(prev.filters, key, value),
    }));
  }, [updateListQuery]);

  const removeSortAt = useCallback((index) => {
    updateListQuery((prev) => ({
      ...prev,
      page: 1,
      sort: (prev.sort ?? []).filter((_, sortIndex) => sortIndex !== index),
    }));
  }, [updateListQuery]);

  const resetListQuery = useCallback(() => {
    updateListQuery((prev) => ({
      page: 1,
      pageSize: prev.pageSize,
      keyword: '',
      sort: [],
      filters: {},
    }));
  }, [updateListQuery]);

  return {
    rows: query.data?.data ?? [],
    pagination: query.data?.page ?? EMPTY_PAGE,
    isLoading: Boolean(mtrStructMainId) && query.isLoading,
    isError: query.isError,
    error: query.error,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    removeSortAt,
    resetListQuery,
    refetch: query.refetch,
  };
}

export default useMtrStructSubList;
